/**
 * Der Transport als Kontext.
 *
 * **Hereingegeben und nicht importiert**, damit ein Test ohne Netz auskommt — dieselbe
 * Entscheidung wie bei der Uhr im Server und beim Zufall in der Engine: was von außen kommt,
 * lässt sich im Test ersetzen, ohne ein Modul zu verbiegen.
 *
 * **Kein State-Management-Paket.** Der Server ist autoritativ, der Client spiegelt; was nicht
 * lokal entschieden wird, braucht keinen lokalen Zustandsspeicher. Ein Kontext für den
 * Transport genügt, und er reicht ihn weiter, ohne ihn zu verstecken.
 */

import { createContext, useContext, type ReactNode } from 'react';

import { createTransport, type Transport } from './transport.js';

const ApiContext = createContext<Transport | undefined>(undefined);

export interface ApiProviderProps {
  readonly children: ReactNode;
  /** Vorbelegt mit dem echten Transport; Tests geben einen eigenen herein. */
  readonly request?: Transport;
}

export function ApiProvider({ children, request }: ApiProviderProps) {
  return <ApiContext.Provider value={request ?? createTransport()}>{children}</ApiContext.Provider>;
}

/**
 * Der Transport für eine Komponente.
 *
 * Wirft **laut**, wenn der Kontext fehlt: ein stiller Rückfall auf den echten Transport würde
 * in einem Test unbemerkt ins Netz greifen, und das wäre genau der Fehler, den der Kontext
 * verhindern soll.
 */
export function useRequest(): Transport {
  const request = useContext(ApiContext);
  if (request === undefined) {
    throw new Error('useRequest braucht einen ApiProvider');
  }
  return request;
}
